import { motion } from "framer-motion";
import { Star, Quote, ArrowLeft, Building2, MapPin, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { PageTransition } from "@/components/PageTransition";

const testimonials = [
  {
    id: 1,
    name: "Alessandro Moretti",
    title: "CEO, Moretti Investments",
    location: "Milan, Italy",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    quote: "Agota Estate demonstrated exceptional professionalism throughout our Monaco acquisition. Their thorough due diligence and meticulous attention to every contractual detail made a complex cross-border transaction remarkably smooth.",
    fullQuote: "Working with Agota Estate was a revelation. From our first consultation to the final handover, every step was handled with precision and care. Their thorough due diligence uncovered issues we would have never caught, saving us significant costs. The meticulous attention to every contractual detail made a complex cross-border transaction remarkably smooth. I've worked with many advisors, but none match Agota's combination of expertise and personal dedication.",
    rating: 5,
    investment: "€8.2M Portfolio",
    propertyType: "Monaco Penthouse",
    year: "2024",
  },
  {
    id: 2,
    name: "Victoria Lindström",
    title: "Private Investor",
    location: "Stockholm, Sweden",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face",
    quote: "What sets Agota Estate apart is their deep understanding of client needs and market dynamics. They identified a Marbella villa that appreciated 40% in two years. Their global network and local expertise are unmatched.",
    fullQuote: "I was skeptical about investing in Spanish real estate from Sweden, but Agota Estate made the entire process seamless. They didn't just find properties — they found THE property that perfectly matched my investment criteria. The Marbella villa they identified appreciated 40% in just two years. Their network of local contacts, from attorneys to property managers, ensured everything was handled professionally. I've since referred three colleagues who all had equally outstanding experiences.",
    rating: 5,
    investment: "€5.5M Portfolio",
    propertyType: "Marbella Villa",
    year: "2023",
  },
  {
    id: 3,
    name: "Omar Al-Rashid",
    title: "Managing Director, Gulf Capital",
    location: "Dubai, UAE",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    quote: "Agota Estate guided our Dubai expansion with confidence and precision. Their expertise in handling complex cross-border regulations and their ability to navigate different business cultures made them invaluable partners.",
    fullQuote: "Expanding our European real estate holdings required a partner who understood both sides of the equation. Agota Estate delivered beyond expectations. Their knowledge of Dubai's unique regulatory landscape, combined with their European expertise, created a seamless bridge for our investments. They navigated cultural nuances with ease and built lasting relationships with our local partners. Their fiduciary commitment to our interests was evident in every negotiation.",
    rating: 5,
    investment: "€12M Portfolio",
    propertyType: "Multiple Properties",
    year: "2024",
  },
  {
    id: 4,
    name: "Isabelle Fontaine",
    title: "Art Collector & Philanthropist",
    location: "Paris, France",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    quote: "Agota Estate doesn't just find properties — they understand the lifestyle you're seeking. Their comprehensive approach across multiple markets is impressive. The Tuscan villa they found perfectly balances privacy with accessibility.",
    fullQuote: "As someone who values both beauty and discretion, finding the right property advisor was paramount. Agota Estate understood immediately that my requirements went beyond square meters and price points. They grasped the lifestyle I was seeking — a place where I could host intimate gatherings, display my art collection, and enjoy complete privacy. The Tuscan estate they found exceeded every expectation. Their ongoing relationship with local artisans and estate managers has made ownership a joy.",
    rating: 5,
    investment: "€6.8M Portfolio",
    propertyType: "Tuscan Estate",
    year: "2023",
  },
  {
    id: 5,
    name: "Henrik Johansson",
    title: "Founder, Nordic Ventures",
    location: "Oslo, Norway",
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face",
    quote: "The level of market insight Agota Estate provides is exceptional. They anticipated market movements in Monaco that resulted in substantial capital appreciation for our portfolio.",
    fullQuote: "When I first approached Agota Estate, I was looking for a straightforward investment property. What I received was a comprehensive wealth strategy that integrated real estate into my broader portfolio. Their market intelligence on Monaco was remarkable — they identified an emerging neighborhood before it became the hottest market in the principality. That single insight resulted in a 35% appreciation in just 18 months. Their proactive approach to portfolio management continues to exceed expectations.",
    rating: 5,
    investment: "€9.4M Portfolio",
    propertyType: "Monaco Apartment",
    year: "2024",
  },
  {
    id: 6,
    name: "Sophia Castellano",
    title: "Fashion Industry Executive",
    location: "London, UK",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face",
    quote: "Agota Estate's white-glove service is unparalleled. From property selection to interior design coordination, they managed every aspect of my Marbella purchase with impeccable taste.",
    fullQuote: "My schedule leaves little time for property hunting, yet Agota Estate made acquiring my Marbella retreat effortless. They curated a selection of properties that matched my aesthetic preferences perfectly — no time wasted on unsuitable viewings. After purchase, they coordinated with interior designers, landscape architects, and local contractors to transform the property before my first visit. Walking into my fully furnished, beautifully designed home was the ultimate luxury experience.",
    rating: 5,
    investment: "€4.2M Portfolio",
    propertyType: "Marbella Contemporary Villa",
    year: "2023",
  },
  {
    id: 7,
    name: "Marcus Weber",
    title: "Investment Banker",
    location: "Frankfurt, Germany",
    image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=150&h=150&fit=crop&crop=face",
    quote: "Agota Estate's financial acumen sets them apart. They structured our Dubai acquisition to maximize tax efficiency while ensuring complete regulatory compliance across jurisdictions.",
    fullQuote: "As someone who works in finance, I have high standards for investment advice. Agota Estate exceeded those standards. Their understanding of cross-border tax implications, currency strategies, and regulatory frameworks is sophisticated and current. The Dubai property they helped me acquire was structured optimally from both a financial and legal perspective. Their network of international tax advisors and attorneys worked seamlessly to protect my interests across multiple jurisdictions.",
    rating: 5,
    investment: "€7.6M Portfolio",
    propertyType: "Dubai Penthouse",
    year: "2024",
  },
  {
    id: 8,
    name: "Elena Petrova",
    title: "Tech Entrepreneur",
    location: "Zurich, Switzerland",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face",
    quote: "The discretion and professionalism of Agota Estate made our high-profile acquisition seamless. They understand that privacy is paramount for their clients.",
    fullQuote: "Privacy was my primary concern when seeking a property advisor. Agota Estate's discretion was evident from our first conversation. They implemented strict confidentiality protocols, used secure communication channels, and ensured that my identity remained protected throughout negotiations. The Italian property they secured for me was handled with such professionalism that the seller never knew my identity until closing. For clients who value privacy, there is simply no better choice.",
    rating: 5,
    investment: "€11.3M Portfolio",
    propertyType: "Italian Coastal Estate",
    year: "2024",
  },
];

const Testimonials = () => {
  return (
    <PageTransition>
      <main className="min-h-screen bg-background">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-b from-secondary/30 to-background">
          <div className="container mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
              >
                <ArrowLeft size={18} />
                <span className="font-body text-sm uppercase tracking-wider">Back to Home</span>
              </Link>
              
              <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-4">
                Client Success Stories
              </p>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-6 leading-tight">
                Trusted by
                <span className="block italic font-medium">Distinguished Investors</span>
              </h1>
              <p className="text-muted-foreground max-w-2xl text-lg leading-relaxed">
                Our clientele includes some of the world's most discerning investors who have 
                entrusted us with their luxury real estate portfolios. Read their stories and 
                discover why Agota Estate is the partner of choice for international property investment.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="py-8 border-y border-border/50">
          <div className="container mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-wrap justify-center gap-8 md:gap-16"
            >
              <div className="text-center">
                <span className="font-display text-3xl md:text-4xl text-primary font-semibold">€65M+</span>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mt-1">Total Client Investments</p>
              </div>
              <div className="text-center">
                <span className="font-display text-3xl md:text-4xl text-primary font-semibold">100%</span>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mt-1">Client Satisfaction</p>
              </div>
              <div className="text-center">
                <span className="font-display text-3xl md:text-4xl text-primary font-semibold">8+</span>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mt-1">Countries Represented</p>
              </div>
              <div className="text-center">
                <span className="font-display text-3xl md:text-4xl text-primary font-semibold">25%</span>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mt-1">Avg. Portfolio Growth</p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Testimonials Grid */}
        <section className="py-20">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group"
                >
                  <div className="relative bg-card border border-border/50 rounded-2xl p-8 lg:p-10 h-full transition-all duration-500 hover:border-primary/30 hover:shadow-luxury">
                    {/* Quote Icon */}
                    <div className="absolute -top-4 left-8">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-lg">
                        <Quote className="w-5 h-5 text-primary-foreground" />
                      </div>
                    </div>

                    {/* Rating & Meta */}
                    <div className="flex items-center justify-between mb-6 pt-2">
                      <div className="flex gap-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                        ))}
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground text-sm">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{testimonial.year}</span>
                      </div>
                    </div>

                    {/* Quote */}
                    <blockquote className="text-foreground/90 leading-relaxed mb-8">
                      "{testimonial.fullQuote}"
                    </blockquote>

                    {/* Property Details */}
                    <div className="flex flex-wrap gap-3 mb-8">
                      <span className="inline-flex items-center gap-1.5 text-xs bg-secondary/50 text-foreground/70 px-3 py-1.5 rounded-full">
                        <Building2 className="w-3.5 h-3.5" />
                        {testimonial.propertyType}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs bg-secondary/50 text-foreground/70 px-3 py-1.5 rounded-full">
                        <MapPin className="w-3.5 h-3.5" />
                        {testimonial.location}
                      </span>
                    </div>

                    {/* Client Info */}
                    <div className="flex items-center gap-4 pt-6 border-t border-border/30">
                      <div className="relative">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-14 h-14 rounded-full object-cover border-2 border-primary/20"
                        />
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-card" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-display text-lg text-foreground">
                          {testimonial.name}
                        </h4>
                        <p className="text-muted-foreground text-sm">
                          {testimonial.title}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-muted-foreground uppercase tracking-wider">
                          Investment
                        </span>
                        <p className="font-display text-primary font-semibold">
                          {testimonial.investment}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-secondary/30">
          <div className="container mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-3xl md:text-4xl text-foreground mb-4">
                Ready to Write Your Success Story?
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto mb-8">
                Join our distinguished clientele and discover extraordinary investment 
                opportunities across Europe and the Middle East.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="luxury" size="xl" asChild>
                  <Link to="/#contact">Schedule Consultation</Link>
                </Button>
                <Button variant="outline" size="xl" asChild>
                  <Link to="/properties">View Properties</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        <Footer />
      </main>
    </PageTransition>
  );
};

export default Testimonials;
